import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowRight, Search } from "lucide-react";
import { NAV_ITEMS, type NavId } from "@/data/nav";
import { useDeliverables, useRisks, useWins } from "@/hooks/useLive";
import { CATEGORIES, STATUS_MAP } from "@/data/categories";
import { cn } from "@/lib/utils";

interface Props {
  open: boolean;
  onClose: () => void;
  onNavigate: (id: NavId) => void;
}

type Item =
  | { kind: "nav"; id: NavId; label: string; hint: string }
  | { kind: "deliverable"; id: string | number; label: string; hint: string }
  | { kind: "risk"; id: number; label: string; hint: string }
  | { kind: "win"; id: number; label: string; hint: string };

export function CommandPalette({ open, onClose, onNavigate }: Props) {
  const DELIVERABLES = useDeliverables();
  const RISKS = useRisks();
  const FELIX_WINS = useWins();
  const [query, setQuery] = useState("");
  const [index, setIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setQuery("");
      setIndex(0);
      const t = setTimeout(() => inputRef.current?.focus(), 10);
      return () => clearTimeout(t);
    }
  }, [open]);

  const items = useMemo<Item[]>(() => {
    const q = query.trim().toLowerCase();
    const nav: Item[] = NAV_ITEMS.map((n) => ({
      kind: "nav" as const,
      id: n.id,
      label: n.label,
      hint: n.description,
    }));
    const deliverables: Item[] = DELIVERABLES.map((d) => ({
      kind: "deliverable" as const,
      id: d.id,
      label: d.name,
      hint: `${CATEGORIES[d.category]?.label ?? d.category ?? "Uncategorized"} · ${d.owner} · ${STATUS_MAP[d.status]?.label ?? d.status ?? "—"}`,
    }));
    const risks: Item[] = RISKS.map((r) => ({
      kind: "risk" as const,
      id: r.id,
      label: r.title,
      hint: `${r.severity.toUpperCase()} · ${r.owner}`,
    }));
    const wins: Item[] = FELIX_WINS.map((w) => ({
      kind: "win" as const,
      id: w.id,
      label: w.quote,
      hint: `Win · ${w.account} · ${w.date}`,
    }));
    const combined = [...nav, ...deliverables, ...risks, ...wins];
    if (!q) return combined.slice(0, 30);
    return combined
      .filter((i) => i.label.toLowerCase().includes(q) || i.hint.toLowerCase().includes(q))
      .slice(0, 30);
  }, [query, DELIVERABLES, RISKS, FELIX_WINS]);

  useEffect(() => {
    setIndex(0);
  }, [query]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setIndex((i) => Math.min(items.length - 1, i + 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setIndex((i) => Math.max(0, i - 1));
      } else if (e.key === "Enter") {
        e.preventDefault();
        const item = items[index];
        if (!item) return;
        if (item.kind === "nav") onNavigate(item.id);
        else if (item.kind === "deliverable") onNavigate("deliverables");
        else if (item.kind === "risk") onNavigate("risks");
        else if (item.kind === "win") onNavigate("wins");
        onClose();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, items, index, onClose, onNavigate]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-foreground/40 backdrop-blur-sm px-4 pt-[14vh]"
      onClick={onClose}
    >
      <div
        className="w-full max-w-xl overflow-hidden rounded-xl border border-border bg-card shadow-card-hover animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b border-border px-4 py-3">
          <Search size={16} className="text-muted-foreground" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Jump to a section, deliverable, risk, or win…"
            className="flex-1 bg-transparent text-body text-foreground placeholder:text-muted-foreground focus:outline-none"
          />
          <kbd className="rounded bg-muted px-1.5 py-0.5 text-micro text-muted-foreground">esc</kbd>
        </div>
        <div className="max-h-[48vh] overflow-y-auto scrollbar-thin">
          {items.length === 0 ? (
            <div className="px-4 py-10 text-center text-caption text-muted-foreground">No matches</div>
          ) : (
            items.map((item, i) => (
              <button
                key={`${item.kind}-${item.id}`}
                onMouseEnter={() => setIndex(i)}
                onClick={() => {
                  if (item.kind === "nav") onNavigate(item.id);
                  else if (item.kind === "deliverable") onNavigate("deliverables");
                  else if (item.kind === "risk") onNavigate("risks");
                  else if (item.kind === "win") onNavigate("wins");
                  onClose();
                }}
                className={cn(
                  "flex w-full items-center gap-3 px-4 py-2.5 text-left",
                  i === index && "bg-accent"
                )}
              >
                <span className="rounded bg-muted px-1.5 py-0.5 text-micro uppercase tracking-wider text-muted-foreground">
                  {item.kind}
                </span>
                <span className="flex-1 truncate text-body text-foreground">{item.label}</span>
                <span className="hidden truncate text-caption text-muted-foreground sm:inline">
                  {item.hint}
                </span>
                <ArrowRight size={14} className="text-muted-foreground" />
              </button>
            ))
          )}
        </div>
        <div className="flex items-center justify-between gap-2 border-t border-border bg-muted/40 px-4 py-2 text-micro text-muted-foreground">
          <span><kbd className="rounded bg-background px-1">↑↓</kbd> navigate</span>
          <span><kbd className="rounded bg-background px-1">↵</kbd> select</span>
          <span><kbd className="rounded bg-background px-1">esc</kbd> close</span>
        </div>
      </div>
    </div>,
    document.body
  );
}
